/**
 * CANONIEKE JSON, VERSIE 1 — de ene serialisatie waarover een hash gaat (C04).
 *
 * C04: "Canonicaliseer JSON met een gedeelde, versieerbare functie;
 * objectvolgorde mag geen andere hash opleveren. Gebruik geen eigen
 * `JSON.stringify`-variant per engine." Dit is die functie. Het
 * release-manifest (`./engine-release`), de configsnapshot (W02b) en elke
 * nieuwe inhoud-hash in de adminregistratie gaan hier doorheen.
 *
 * ── DE REGELS VAN `canonical-json@1` ────────────────────────────────────────
 *  - **Objecten**: eerst de ARRAY-INDEX-sleutels (`"0"`, `"2"`, `"10"`: de
 *    canonieke decimale vorm van 0 t/m 4294967294) numeriek oplopend, daarna
 *    alle andere sleutels op UTF-16-code-units (de gewone
 *    `Array.prototype.sort`). Een sleutel met waarde `undefined` telt als
 *    AFWEZIG; `null` is een waarde en blijft staan. Alleen gewone objecten
 *    (prototype `Object.prototype` of `null`); de sleutel `__proto__` is een
 *    fout (zie hieronder).
 *
 *    Die volgorde is BEWUST niet die van RFC 8785 (die sorteert ook `"10"`
 *    vóór `"2"`). Ze is de volgorde die de drie bestaande serialisaties
 *    feitelijk opleveren: zij bouwen een object in gesorteerde volgorde en
 *    `JSON.stringify` zet array-indexen daarin altijd voorop. Zo kan elke
 *    bestaande hash met deze functie herrekend worden zonder één byte
 *    verschil — en dat weegt zwaarder dan aansluiting op een RFC die geen van
 *    onze lezers gebruikt.
 *  - **Arrays**: de volgorde blijft staan — die draagt betekenis (paden,
 *    stappen). `undefined` of een gat in een array is een FOUT: `JSON.stringify`
 *    maakt er stil `null` van, en dan hashen `[undefined]` en `[null]` gelijk.
 *  - **Getallen**: alleen eindige; de notatie is die van ECMAScript
 *    (`Number.prototype.toString`, zoals RFC 8785): `1.0` → `1`, `-0` → `0`,
 *    `1e21` → `1e+21`. `NaN` en `±Infinity` zijn een FOUT (niet stil `null`).
 *  - **Tekst**: de escapes van `JSON.stringify` (ES2019, ook voor losse
 *    surrogaten). GEEN Unicode-normalisatie: `é` als één teken en als `e` +
 *    accent zijn twee verschillende inhouden, en dat zijn ze ook voor wie het
 *    bestand leest.
 *  - **Al het andere** — `Date`, `Map`, `Set`, klasse-instanties, buffers,
 *    `bigint`, functies, symbolen, symboolsleutels, kringverwijzingen en de
 *    sleutel `__proto__` — is een FOUT met het pad erbij. Een `Date` zou
 *    anders `{}` worden en elke datum dezelfde hash geven; `__proto__` laten
 *    de bestaande serialisaties stil verdwijnen (hun `out[k] = …` zet het
 *    prototype in plaats van een veld).
 *
 * ── WAAROM DE BESTAANDE DRIE NIET VERVANGEN WORDEN ──────────────────────────
 * Er bestaan al drie sorteer-serialisaties: `stableStringify` in
 * `packages/ocr/src/trace-cache.ts` (cachesleutel), `stableDesignStringify` in
 * `apps/runtime/lib/editor/design-revision.ts` (`contentHash` van een
 * ontwerprevisie) en `stableStringify` in
 * `apps/runtime/lib/resolved-settings-profile.ts` (`outputFingerprint`). Voor
 * elke waarde die deze functie ACCEPTEERT geven ze byte voor byte dezelfde
 * tekst — dat bewijst `canonical-json.test.ts` tegen hun algoritme. Het verschil
 * zit alleen in wat ze stil doorlaten (`NaN` → `null`, `Date` → `{}`).
 *
 * Toch worden ze hier niet vervangen: hun uitvoer zit in BEWAARDE hashes
 * (revisies in de database, cachesleutels in Blob). Een aanroeper die
 * overstapt verandert niets aan geldige invoer, maar gaat wél gooien op
 * invoer die vandaag stil een hash krijgt — dat is een gedragswijziging in de
 * lane van runtime en ocr, niet iets wat een contract ongevraagd doet.
 *
 * ── VERSIE ──────────────────────────────────────────────────────────────────
 * `CANONIEKE_JSON_ID` gaat MEE in wat gehasht wordt (het manifest draagt hem).
 * Een wijziging aan één regel hierboven is `canonical-json@2`, een nieuwe
 * functie ernaast; `@1` blijft bestaan zolang er een hash van bewaard is.
 */

/** De versie-id van deze regels; gaat mee in elk object dat ermee gehasht wordt. */
export const CANONIEKE_JSON_ID = "canonical-json@1" as const;
export type CanoniekeJsonId = typeof CANONIEKE_JSON_ID;

/** Een waarde die niet canoniek te schrijven is. `pad` wijst hem aan (`$.engines[2].codeDigest`). */
export class CanoniekeJsonFout extends TypeError {
  readonly pad: string;
  constructor(pad: string, reden: string) {
    super(`canonical-json@1: ${reden} (op ${pad}).`);
    this.name = "CanoniekeJsonFout";
    this.pad = pad;
  }
}

/**
 * De canonieke tekst van `waarde` volgens `canonical-json@1`.
 *
 * @throws {CanoniekeJsonFout} bij alles wat geen JSON-waarde is (zie de kop).
 */
export function canoniekeJson(waarde: unknown): string {
  return schrijf(waarde, "$", new Set<object>());
}

function schrijf(waarde: unknown, pad: string, stapel: Set<object>): string {
  if (waarde === null) return "null";
  switch (typeof waarde) {
    case "string":
      return JSON.stringify(waarde);
    case "boolean":
      return waarde ? "true" : "false";
    case "number":
      if (!Number.isFinite(waarde)) {
        throw new CanoniekeJsonFout(pad, `${String(waarde)} is geen eindig getal`);
      }
      // `JSON.stringify` volgt Number.prototype.toString en schrijft -0 als "0".
      return JSON.stringify(waarde);
    case "undefined":
      throw new CanoniekeJsonFout(pad, "undefined is geen waarde");
    case "bigint":
    case "function":
    case "symbol":
      throw new CanoniekeJsonFout(pad, `een ${typeof waarde} is geen JSON-waarde`);
    default:
      break;
  }

  const object = waarde as object;
  if (stapel.has(object)) {
    throw new CanoniekeJsonFout(pad, "kringverwijzing");
  }

  if (Array.isArray(object)) {
    stapel.add(object);
    const delen: string[] = [];
    for (let i = 0; i < object.length; i++) {
      const plek = `${pad}[${i}]`;
      if (!Object.prototype.hasOwnProperty.call(object, i)) {
        throw new CanoniekeJsonFout(plek, "gat in een array");
      }
      const item: unknown = object[i];
      if (item === undefined) {
        throw new CanoniekeJsonFout(plek, "undefined in een array (JSON.stringify maakt er stil null van)");
      }
      delen.push(schrijf(item, plek, stapel));
    }
    stapel.delete(object);
    return `[${delen.join(",")}]`;
  }

  const prototype: unknown = Object.getPrototypeOf(object);
  if (prototype !== Object.prototype && prototype !== null) {
    const naam = (object as { constructor?: { name?: unknown } }).constructor?.name;
    throw new CanoniekeJsonFout(
      pad,
      `alleen gewone objecten, geen ${typeof naam === "string" && naam ? naam : "klasse-instantie"}`,
    );
  }
  if (Object.getOwnPropertySymbols(object).length > 0) {
    throw new CanoniekeJsonFout(pad, "een symboolsleutel valt stil weg");
  }

  stapel.add(object);
  const record = object as Record<string, unknown>;
  const delen: string[] = [];
  for (const sleutel of gesorteerdeSleutels(record, pad)) {
    const kind = record[sleutel];
    if (kind === undefined) continue;
    delen.push(`${JSON.stringify(sleutel)}:${schrijf(kind, `${pad}.${sleutel}`, stapel)}`);
  }
  stapel.delete(object);
  return `{${delen.join(",")}}`;
}

const ARRAY_INDEX = /^(?:0|[1-9][0-9]*)$/;
const MAX_ARRAY_INDEX = 4294967294;

/** Is dit een sleutel die JavaScript als array-index voorop zet? */
function isArrayIndex(sleutel: string): boolean {
  return ARRAY_INDEX.test(sleutel) && Number(sleutel) <= MAX_ARRAY_INDEX;
}

/** Array-indexen numeriek oplopend, daarna de rest op UTF-16-code-units (zie de kop). */
function gesorteerdeSleutels(record: Record<string, unknown>, pad: string): string[] {
  const indexen: string[] = [];
  const overig: string[] = [];
  for (const sleutel of Object.keys(record)) {
    if (sleutel === "__proto__") {
      throw new CanoniekeJsonFout(`${pad}.__proto__`, "de sleutel __proto__ is niet toegestaan");
    }
    (isArrayIndex(sleutel) ? indexen : overig).push(sleutel);
  }
  indexen.sort((a, b) => Number(a) - Number(b));
  overig.sort();
  return [...indexen, ...overig];
}

/* ── WebCrypto, zonder DOM-types ────────────────────────────────────────────
 * Zelfde afweging als in `./webhooks`: dit package compileert tegen
 * `lib: ES2022` en draait ook buiten Node (edge, browser, Deno). De twee
 * kleinste vormen die we echt aanroepen, en één melding als ze ontbreken. */

interface MinimaleDigest {
  digest(algoritme: "SHA-256", data: Uint8Array): Promise<ArrayBuffer>;
}

interface MinimaleEncoder {
  encode(tekst: string): Uint8Array;
}

function webcrypto(): { subtle: MinimaleDigest; encoder: MinimaleEncoder } {
  const g = globalThis as {
    crypto?: { subtle?: MinimaleDigest };
    TextEncoder?: new () => MinimaleEncoder;
  };
  if (!g.crypto?.subtle || !g.TextEncoder) {
    throw new Error(
      "canonical-json@1: WebCrypto (globalThis.crypto.subtle en TextEncoder) ontbreekt. " +
        "Gebruik Node 20 of nieuwer, Deno, Bun, een edge-runtime of een moderne browser.",
    );
  }
  return { subtle: g.crypto.subtle, encoder: new g.TextEncoder() };
}

/** SHA-256 over de UTF-8-bytes van een tekst, als 64 hex in kleine letters. */
export async function sha256Hex(tekst: string): Promise<string> {
  const { encoder } = webcrypto();
  return sha256HexVanBytes(encoder.encode(tekst));
}

/**
 * SHA-256 over bytes, als 64 hex in kleine letters. Voor hashinvoer die geen
 * tekst is (de pixelbytes van een tekstmasker, `./text-analysis`); dezelfde
 * WebCrypto-toegang als `sha256Hex`, zodat er één plek is die hem opzoekt.
 */
export async function sha256HexVanBytes(bytes: Uint8Array): Promise<string> {
  const { subtle } = webcrypto();
  const digest = new Uint8Array(await subtle.digest("SHA-256", bytes));
  let uit = "";
  for (const b of digest) uit += b.toString(16).padStart(2, "0");
  return uit;
}

/**
 * Een UUID versie 8 (RFC 9562, "eigen opbouw") uit de eerste 128 bits van een
 * sha256 in hex: versie-nibble 8, variant `10xx`, kleine letters. Voor een id
 * dat uit een FEIT volgt en bij elke herhaling gelijk moet zijn — het
 * webhookbericht van een productiejob (`@mark2form/jobs`) en het job-id bij een
 * `Idempotency-Key` (`productieJobIdVoorIdempotencyKey`, K15). Dezelfde opbouw
 * als `eventIdVoor` in `@mark2form/webhooks`; één plek, zodat er geen tweede
 * variant naast gaat lopen.
 */
export function uuidV8UitSha256Hex(hex: string): string {
  if (!/^[0-9a-f]{32,}$/.test(hex)) throw new Error("uuidV8UitSha256Hex: verwacht minstens 32 hex-tekens in kleine letters.");
  const bytes = hex.slice(0, 32).match(/../g)!.map((b) => Number.parseInt(b, 16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x80; // versie 8
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant 10xx (RFC 9562)
  const h = bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * `sha256(canoniekeJson(waarde))` als 64 hex. Asynchroon, omdat WebCrypto dat
 * is. Wie de hash in Node synchroon nodig heeft, zet `createHash("sha256")`
 * over `canoniekeJson(waarde)`: de TEKST is het contract, de hashfunctie de
 * implementatie (zelfde afspraak als `downloadIdempotencyInvoer` in `./outbox`).
 */
export async function canoniekeJsonSha256(waarde: unknown): Promise<string> {
  return sha256Hex(canoniekeJson(waarde));
}
